<script lang="ts">
  import { CheckCircle, XCircle, HelpCircle } from 'lucide-svelte';
  import { scale, fade } from 'svelte/transition';
  import Button from '../ui/button.svelte';
  import type { ExerciseItem, EvaluationResult, UserAnswer, ClozeAnswer } from '../../types/exercise';
  import { _ } from 'svelte-i18n';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange }: Props = $props();

  // Local state
  let userInput = $state('');
  let showHint = $state(false);

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'CLOZE_SMART' ? exercise.modeData : null
  );

  // Split sentence into parts (before gap, gap, after gap)
  let sentenceParts = $derived(() => {
    if (!modeData) return { before: '', after: '' };

    const sentence = modeData.sentenceWithGap;
    const gapIndex = sentence.indexOf('_____');

    if (gapIndex === -1) {
      return { before: sentence, after: '' };
    }

    return {
      before: sentence.slice(0, gapIndex),
      after: sentence.slice(gapIndex + 5), // 5 = length of "_____"
    };
  });

  // Update parent when input changes
  $effect(() => {
    if (userInput.trim()) {
      const answer: ClozeAnswer = {
        type: 'CLOZE_SMART',
        text: userInput,
      };
      onAnswerChange(answer);
    }
  });

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id) {
      userInput = '';
      showHint = false;
    }
  });

  // Get hint (first letter)
  function getHint(): string {
    if (!modeData) return '';
    return modeData.gapWord.charAt(0) + '...';
  }
</script>

<div class="space-y-8">
  <!-- Instructions -->
  <div class="text-center" in:scale={{ duration: 300, start: 0.95 }}>
    <h2 class="text-lg font-semibold text-muted-foreground mb-4">
      {$_('exercise.fill_missing_word')}
    </h2>
  </div>

  <!-- Sentence with gap -->
  {#if modeData}

    <div class="p-8 bg-secondary/10 backdrop-blur-sm rounded-2xl border border-border/50 shadow-sm relative overflow-hidden">
        <!-- Decoration -->
        <div class="absolute top-0 right-0 p-32 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

      <div class="text-2xl leading-loose text-center font-medium relative z-10">
        <span>{sentenceParts().before}</span>
        {#if !showFeedback}
          <div class="inline-block relative mx-1 top-2">
            <input
                type="text"
                bind:value={userInput}
                placeholder="..."
                class="inline-block w-40 px-2 py-1 text-center text-2xl font-bold
                       border-b-4 border-primary/50 bg-secondary/10 outline-none text-primary rounded-t-lg
                       focus:border-primary focus:bg-background transition-all placeholder:text-muted-foreground/30"
              />
          </div>
        {:else}
          <span
            in:scale={{ duration: 300 }}
            class="inline-block px-3 py-1 mx-1 rounded-lg font-bold border-b-4
                   {evaluationResult?.isCorrect
                     ? 'bg-green-100 dark:bg-green-500/40 text-green-700 dark:text-green-50 border-green-500'
                     : 'bg-red-100 dark:bg-red-500/40 text-red-700 dark:text-red-50 border-red-500'}"
          >
            {evaluationResult?.isCorrect ? userInput : modeData.gapWord}
          </span>
        {/if}
        <span>{sentenceParts().after}</span>
      </div>
    </div>
  {/if}

  <!-- Hint button -->
  {#if !showFeedback && modeData}
    <div class="text-center h-10">
      {#if !showHint}
        <Button
          onclick={() => (showHint = true)}
          variant="ghost"
          size="sm"
          class="text-muted-foreground hover:text-primary"
        >
          <HelpCircle class="w-4 h-4 mr-2" />
          {$_('exercise.hint')}
        </Button>
      {:else}
        <div in:fade={{ duration: 200 }} class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/50 text-secondary-foreground text-sm font-medium">
          <HelpCircle class="w-4 h-4 opacity-70" />
          {$_('exercise.hint_label')} <strong class="text-primary">{getHint()}</strong>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Original word context -->
  <div class="text-center bg-secondary/20 py-3 rounded-xl backdrop-blur-sm">
    <p class="text-sm text-secondary-foreground">
        {$_('exercise.target_word')} <strong class="text-lg ml-1 text-foreground">{exercise.vocabItem.primary}</strong>
    </p>
  </div>

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-l-4 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-start gap-3">
        {#if evaluationResult.isCorrect}
            <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
                <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.correct')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1 opacity-90">
                    {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <p class="opacity-90">{$_('exercise.great_job')}</p>
              {/if}
            </div>
        {:else}
            <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
                <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.incorrect')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1">
                   {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <span class="font-semibold text-lg block mt-1">
                    {$_('exercise.correct_answer_label')} {evaluationResult.correctAnswer}
                 </span>
              {/if}
            </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
